<script setup lang="ts">
import { AppRoute } from '@shared/config'
import { AppText, AppTextLink } from '@shared/ui/typography'

import { useAssignmentEditorPage } from '../model/assignments/assignment-editor.hook'
import AssignmentHeader from './assignments/editor/AssignmentHeader.vue'
import AssignmentValidationReport from './assignments/editor/AssignmentValidationReport.vue'
import AssignmentTaxonomyPicker from './assignments/editor/AssignmentTaxonomyPicker.vue'
import AssignmentCoverDetails from './assignments/editor/AssignmentCoverDetails.vue'
import ChapterList from './assignments/editor/ChapterList.vue'
import ChapterEditor from './assignments/editor/ChapterEditor.vue'
import ExerciseList from './assignments/editor/math/ExerciseList.vue'
import ExerciseEditor from './assignments/editor/math/ExerciseEditor.vue'

defineOptions({
  name: 'AssignmentDetailsPage',
})

const {
  isNotFound,
  hasDocument,
  refCanView,
  refCanManage,
  isEditable,
  isMath,
  blockers,
  groups,
  summary,
  hasProblems,
  hasValidated,
  isReportStale,
  load,
  save,
  publish,
  validate,
  focusIssue,
  confirmLeave,
  confirmRouteUpdate,
  leave,
} = useAssignmentEditorPage()

onMounted(() => {
  void load()
})

// Guards only; cleanup is `onUnmounted`. Vue Router reuses this page for `/content/assignments/[id]`
// changes, which the leave guard never sees — the update guard covers those.
onBeforeRouteLeave(() => confirmLeave())
onBeforeRouteUpdate((to, from) => confirmRouteUpdate(to, from))

onUnmounted(() => {
  leave()
})
</script>

<template>
  <div class="assignment-editor">
    <!-- `view-assignment-templates` is enforced server-side (contract § 2); this only spares an
         admin who lands here by URL a wall of 403 toasts. -->
    <div
      v-if="!refCanView"
      class="assignment-editor__empty"
    >
      <AppText name="body1-medium">
        You do not have access to assignment templates.
      </AppText>
    </div>

    <div
      v-else-if="isNotFound"
      class="assignment-editor__empty"
    >
      <AppText name="body1-medium">
        Assignment not found
      </AppText>
      <AppText
        name="body2"
        color="slate-500"
      >
        The address does not point at an assignment template that exists.
      </AppText>
      <AppTextLink
        name="body2"
        :href="AppRoute.Assignments"
      >
        Back to assignments
      </AppTextLink>
    </div>

    <template v-else-if="!isEditable">
      <AssignmentHeader :can-manage="refCanManage" />

      <!-- A type the API knows and this app does not: read-only rather than a guessed editor. -->
      <div class="assignment-editor__placeholder">
        <AppText name="body1-medium">
          This template type has no editor here
        </AppText>
        <AppText
          name="body2"
          color="slate-500"
        >
          Update app-admin to edit it.
        </AppText>
        <AppTextLink
          name="body2"
          :href="AppRoute.Assignments"
        >
          Back to assignments
        </AppTextLink>
      </div>
    </template>

    <template v-else-if="hasDocument">
      <AssignmentHeader
        :can-manage="refCanManage"
        @click:save="save"
        @click:validate="validate"
        @click:publish="publish"
      />

      <AssignmentValidationReport
        :has-validated="hasValidated"
        :summary="summary"
        :has-problems="hasProblems"
        :is-stale="isReportStale"
        :blockers="blockers"
        :groups="groups"
        @click:issue="focusIssue"
      />

      <!-- One card each, because they answer different questions: the curriculum card is where
           the template sits in the syllabus (an FK, decision 6), the cover card is what a kid sees
           on the shelf. Neither belongs inside a chapter. -->
      <AssignmentTaxonomyPicker />
      <AssignmentCoverDetails />

      <!-- MATH: outline and the selected exercise; its prompt carries its own KaTeX preview tab. -->
      <div
        v-if="isMath"
        class="assignment-editor__columns"
      >
        <ExerciseList class="assignment-editor__rail" />
        <ExerciseEditor class="assignment-editor__form" />
      </div>

      <!-- READING: outline and passage. There is no third column and no activities section:
           decision 17 leaves it out entirely rather than collapsing it. -->
      <div
        v-else
        class="assignment-editor__columns"
      >
        <ChapterList class="assignment-editor__rail" />
        <ChapterEditor class="assignment-editor__form" />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss" src="./AssignmentDetailsPage.scss" />
